<script lang="ts">
  import { invalidate } from '$app/navigation';
  import { page } from '$app/stores';
  import GreyhoundRacingIcon from '$components/icons/GreyhoundRacingIcon.svelte';
  import HarnessRacingIcon from '$components/icons/HarnessRacingIcon.svelte';
  import HorseRacingIcon from '$components/icons/HorseRacingIcon.svelte';
  import type { Race, RacesResponse } from '$types/races';
  import { isSubscribed } from '$utils/auth';
  import { isFiltering, raceFilters } from '$utils/filters';
  import { AustralianStates, raceTypeBadgeClass } from '$utils/race';
  import { DatePicker } from 'date-picker-svelte';
  import QuickFilterButton from './QuickFilterButton.svelte';
  import { loadingRaces, scrollToActiveRace } from './RaceList.svelte';

  $: ({ races } = $page.data);

  $: filteredMeetings =
    $races?.data?.races
      ?.filter((r) => {
        return r.meeting?.type && r.meeting.track && r.meeting.location;
      })
      ?.map((r) => r.meeting)
      // filter out any meetings with the same type and track
      .filter((m, i, arr) => {
        const key = `${m.type}-${m.track}-${m.location}`;
        return (
          arr.findIndex((m2) => {
            const key2 = `${m2.type}-${m2.track}-${m2.location}`;
            return key === key2;
          }) === i
        );
      })
      .filter((m) => {
        if (($raceFilters.locations?.length ?? 0) > 0) {
          if ($raceFilters.locations?.includes('ANZ')) {
            if (Object.values(AustralianStates).includes(m.location))
              return false;
            if (m.location === 'NZL') return false;
          }
          if ($raceFilters.locations?.includes('International')) {
            if (
              !Object.values(AustralianStates).includes(m.location) &&
              m.location !== 'NZL'
            )
              return false;
          }
        }
        if ($raceFilters.types.length > 0) {
          const typeMap = {
            ['Harness']: 'H',
            ['Greyhound']: 'G',
            ['Thoroughbred']: 'T'
          };
          if (!m?.type) return false;
          if (!$raceFilters.types.includes(typeMap[m.type])) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        const keyA = `${a.type}-${a.track}-${a.location}`;
        const keyB = `${b.type}-${b.track}-${b.location}`;
        return keyA.localeCompare(keyB);
      }) ?? [];

  $: filteredMsg = $raceFilters.ids.length
    ? `Manual filter active (${$raceFilters.ids.length} races)`
    : 'Filters active';
  $: msg = filteredMsg;

  let showDatePicker = false;
  let showMeetingPicker = false;
  let date = $raceFilters.dates?.[0] ? new Date($raceFilters.dates[0]) : null;

  // create some min and max dates for the date picker
  // TODO allow this to be dynamic depending on the user's subscription
  let minDate = new Date();
  let maxDate = new Date();
  //   subtract 7 days
  $: {
    if ($isSubscribed) {
      // set date to november 06
      minDate.setMonth(10);
      minDate.setDate(6);
      minDate.setFullYear(2022);
    }
    maxDate.setMonth(maxDate.getMonth() + 1);
  }

  async function click() {
    raceFilters.update((f) => {
      f.ids = [];
      return f;
    });
    await invalidate('betwatch:races');
    await $scrollToActiveRace();
  }

  $: isRaceTypeFiltered = (type: 'G' | 'H' | 'R' | 'T') => {
    // accept both T and R for racing
    if (type === 'R') type = 'T';
    // check if type in in filters
    const types = $raceFilters.types;
    if (types?.length ?? 0 > 0) {
      return types.includes(type);
    }
    return false;
  };

  $: isMeetingFiltered = (meeting: Race['meeting']) => {
    // check if meeting in in filters
    const meetingIds = $raceFilters.meetings;
    if (meetingIds?.length ?? 0 > 0) {
      const key = `${meeting.type}-${meeting.track}-${meeting.location}`;
      return meetingIds.includes(key);
    }
    return false;
  };
</script>

<div class="relative flex flex-row justify-center pb-2 pl-2 text-sm">
  <QuickFilterButton
    title="Date"
    override={true}
    classProps="absolute z-40 mt-9 items-center bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none rounded-md"
    buttonClassProps={$raceFilters.dates.length
      ? 'ring-orange-200 bg-orange-100 hover:bg-orange-200 focus:border-orange-600 '
      : ''}
    bind:show={showDatePicker}
  >
    <div class="flex flex-col">
      {#if date}
        <button
          on:click={async () => {
            raceFilters.updateRaceDateFilter(null);
            date = null;
            showDatePicker = false;
            await invalidate('betwatch:races');
          }}
          on:keypress={async () => {
            raceFilters.updateRaceDateFilter(null);
            date = null;
            showDatePicker = false;
            await invalidate('betwatch:races');
          }}
          class="cursor-pointer bg-orange-100 p-2 text-center hover:bg-orange-200"
        >
          Filtering on {date.toLocaleDateString('en-AU')} (Clear)
        </button>
      {/if}
      <DatePicker
        bind:value={date}
        on:select={async () => {
          loadingRaces.set(true);
          raceFilters.updateRaceDateFilter(date);
          showDatePicker = false;
          await invalidate('betwatch:races');
        }}
        min={minDate}
        max={maxDate}
      />
    </div>
  </QuickFilterButton>
  <QuickFilterButton
    title="Type"
    buttonClassProps={$raceFilters.types.length
      ? 'ring-orange-200 bg-orange-100 hover:bg-orange-200 focus:border-orange-600 '
      : ''}
  >
    <div class="flex flex-row">
      <button
        on:click={() => raceFilters.toggleRaceTypeFilter('T')}
        on:keypress={() => raceFilters.toggleRaceTypeFilter('T')}
        class="{!isRaceTypeFiltered('T')
          ? 'bg-orange-50'
          : 'bg-orange-300'} my-2 ml-2 flex h-20 flex-1 cursor-pointer rounded-md hover:bg-orange-200"
      >
        <div class="m-auto flex flex-col items-center">
          <HorseRacingIcon
            size={25}
            colour={!isRaceTypeFiltered('T') ? '#555' : '#000'}
          />
          <span
            class="{!isRaceTypeFiltered('T')
              ? 'text-gray-500'
              : 'text-gray-900'} text-xs"
            >{isRaceTypeFiltered('T') ? '✅ ' : ''}Racing</span
          >
        </div>
      </button>
      <button
        on:click={() => raceFilters.toggleRaceTypeFilter('G')}
        on:keypress={() => raceFilters.toggleRaceTypeFilter('G')}
        class="my-2 ml-2 flex h-20 flex-1 cursor-pointer rounded-md {!isRaceTypeFiltered(
          'G'
        )
          ? 'bg-blue-50'
          : 'bg-blue-300'}  hover:bg-blue-200"
      >
        <div class="m-auto flex flex-col items-center">
          <GreyhoundRacingIcon
            size={25}
            colour={!isRaceTypeFiltered('G') ? '#555' : '#000'}
          />
          <span class="text-xs text-gray-500"
            >{isRaceTypeFiltered('G') ? '✅ ' : ''}Greyhound</span
          >
        </div>
      </button>
      <button
        on:click={() => raceFilters.toggleRaceTypeFilter('H')}
        on:keypress={() => raceFilters.toggleRaceTypeFilter('H')}
        class=" my-2 ml-2 mr-2 flex h-20 flex-1 cursor-pointer rounded-md {!isRaceTypeFiltered(
          'H'
        )
          ? 'bg-green-50'
          : 'bg-green-300'}  hover:bg-green-200"
      >
        <div class="m-auto flex flex-col items-center">
          <HarnessRacingIcon
            size={25}
            colour={!isRaceTypeFiltered('H') ? '#555' : '#000'}
          />
          <span class="text-xs text-gray-500"
            >{isRaceTypeFiltered('H') ? '✅ ' : ''}Harness</span
          >
        </div>
      </button>
    </div>
  </QuickFilterButton>
  <QuickFilterButton
    title="Meeting"
    bind:show={showMeetingPicker}
    buttonClassProps={$raceFilters.meetings.length ||
    $raceFilters.locations?.length
      ? 'ring-orange-200 bg-orange-100 hover:bg-orange-200 focus:border-orange-600 '
      : ''}
  >
    {#if $raceFilters.meetings.length}
      <button
        on:click={() => {
          raceFilters.update((f) => {
            f.meetings = [];
            return f;
          });
          showMeetingPicker = false;
        }}
        on:keypress={() => {
          raceFilters.update((f) => {
            f.meetings = [];
            return f;
          });
          showMeetingPicker = false;
        }}
        class="flex w-full cursor-pointer justify-center bg-orange-100 from-orange-300 to-orange-200 p-2 text-center font-semibold hover:bg-gradient-to-b"
      >
        Filtering {$raceFilters.meetings.length} meetings (Clear)
      </button>
    {/if}
    <ul class="h-96 overflow-y-scroll">
      <div class="flex w-full flex-row justify-center text-xs">
        <button
          class="m-1 flex flex-grow cursor-pointer items-center justify-center rounded-md border border-blue-200 bg-gradient-to-br from-blue-100 to-blue-200 p-2 text-center hover:from-blue-200 hover:to-blue-300"
          class:opacity-20={$raceFilters.locations?.find((l) => l === 'ANZ')}
          on:click={() => raceFilters.toggleRaceLocationFilter('ANZ')}
          on:keypress={() => raceFilters.toggleRaceLocationFilter('ANZ')}
        >
          🇦🇺 AUS / NZ
        </button>
        <button
          class="m-1 flex flex-grow cursor-pointer items-center justify-center rounded-md border border-green-200 bg-gradient-to-br from-green-100 to-green-200 p-2 text-center hover:from-green-200 hover:to-green-300"
          class:opacity-20={$raceFilters.locations?.find(
            (l) => l === 'International'
          )}
          on:click={() => raceFilters.toggleRaceLocationFilter('International')}
          on:keypress={() =>
            raceFilters.toggleRaceLocationFilter('International')}
        >
          🌎 International
        </button>
      </div>
      {#each filteredMeetings as meeting (meeting.id)}
        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
        <li
          on:click={() => raceFilters.toggleRaceMeetingFilter(meeting)}
          on:keypress={() => raceFilters.toggleRaceMeetingFilter(meeting)}
          class="flex cursor-pointer items-center space-x-3 border-b py-2 hover:bg-sky-50"
        >
          <div class="flex flex-shrink-0 flex-row items-center pl-3">
            <input
              type="checkbox"
              checked={isMeetingFiltered(meeting)}
              class="mr-1 h-4 w-4 rounded border-gray-100 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500"
            />
            <div
              class="center ml-1 mr-2 items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize shadow-sm ring-1 {raceTypeBadgeClass(
                meeting
              )}"
            >
              {meeting.type?.[0].replace('T', 'R')}
            </div>
            <div class="min-w-0 flex-1">
              <span class="text-sm text-gray-900">
                {meeting?.track}
                {#if !Object.values(AustralianStates).includes(meeting?.location ?? '')}
                  <span class="text-xs text-gray-500"
                    >({meeting?.location})</span
                  >
                {/if}
              </span>
            </div>
          </div>
        </li>
      {/each}
    </ul>
  </QuickFilterButton>
</div>

{#if $isFiltering}
  <button
    on:click={() => isFiltering.update((f) => !f)}
    on:keypress={() => isFiltering.update((f) => !f)}
    class="w-full cursor-pointer bg-blue-100 p-2 text-center text-sm hover:bg-blue-200"
  >
    Apply Manual Race Filter
  </button>
{:else if $raceFilters.ids.length}
  <button
    on:pointerenter={() => (msg = 'Clear Filters')}
    on:pointerleave={() => (msg = filteredMsg)}
    on:click={click}
    on:keypress={click}
    class="w-full cursor-pointer bg-orange-100 p-2 text-center text-sm hover:bg-orange-200"
  >
    {msg}
  </button>
{/if}
